import { type AxiosResponse, AxiosHeaders } from 'axios'
import type { TApiResponse } from '@/types/api-types/TApiResponse'
import {
  CustomFilterTypeExpression,
  ListFilterTypeExpression,
  ListPagingTypeExpression,
  ListResponseTypeExpression
} from '@/enum/LinqExpressionType'
import type { TAccountAccessResponse } from '@/types/api-types/account'
import CookieStorageService from '@/services/CookieStorageService'
import { DEFAULT_COOKIE_MAX_AGE } from '../constants'
import LocalStorageService from '@/services/LocalStorageService'
import type { TItemRequestQueryParams, TListRequestQueryParams } from '@/types/api-types'
import { generateRandomResources } from '@/utils/helpers'
import {
  JobExpandablePropertyType,
  JobTasksFilterType,
  JobType,
  ResourceType
} from '@/enum'
import { DEFAULT_PAGE_LIMIT, DEFAULT_PAGE_OFFSET } from '@/utils/constants'
import StatusCodeType from '@/enum/StatusCodeType'

export const fetchUntilExhaustion = async (
  request: (params: any) => TApiResponse,
  params: any
): Promise<any[]> => {
  const response = await request({
    ...params
  })
  let recursiveResponse: any[] = []
  if (response?.data) {
    const { limit, size, offset } = response.data

    if (offset + limit < size) {
      recursiveResponse = await fetchUntilExhaustion(request, {
        ...params,
        [ListPagingTypeExpression.Offset]: offset + limit
      })
    }
    if (response.data.value) {
      return Promise.resolve([...response.data.value, ...recursiveResponse])
    }
  }
  return Promise.resolve([])
}

export const saveDataToCookieStorage = (data: TAccountAccessResponse) => {
  const cookieStorageService = new CookieStorageService()
  cookieStorageService.setData('userId', data.userId, DEFAULT_COOKIE_MAX_AGE)
  cookieStorageService.setData('accessToken', data.accessToken, DEFAULT_COOKIE_MAX_AGE)
  cookieStorageService.setData('refreshToken', data.refreshToken, data.refreshTokenExpiresInSeconds)
  cookieStorageService.setData('expiringAt', data.accessTokenExpirationUtc, DEFAULT_COOKIE_MAX_AGE)
}

export const saveUserIdToLocalStorage = (userId: string) => {
  const localStorageService = new LocalStorageService()

  localStorageService.setData('userId', userId)
}

export const generateFilterExpressionForCalendarEvents = (
  type: JobType,
  start: string,
  end: string,
  options?: any
): string => {
  const {
    [ListFilterTypeExpression.SearchText]: searchText,
    [ListFilterTypeExpression.Filter]: filterOptions
  } = options ?? {}
  const dateProperties = [
    // JobsFilterType.SCHEDULED_DATE,
    JobTasksFilterType.DUE_AT,
    JobTasksFilterType.STARTED_AT,
    JobTasksFilterType.COMPLETED_AT
  ]
  let responseOptions: any = {
    [ListResponseTypeExpression.Expand]: `
    ${JobExpandablePropertyType.ASSIGNED_EMPLOYEES},
    ${JobExpandablePropertyType.ASSIGNED_EMPLOYEES_EMPLOYEE},
    ${JobExpandablePropertyType.JOB}`,
    [ListFilterTypeExpression.Filter]: {
      ...filterOptions,
      // [JobTasksFilterType.JOB_TYPE]: type,
      [CustomFilterTypeExpression.Expression]: `(${[
        ...dateProperties.map((property) => `${property}>="${start}"&&${property}<="${end}"`),
        // `"${start}">=${JobTasksFilterType.SCHEDULED_DATE}&&"${start}"<=${JobTasksFilterType.NEXT_TRIGGER_INFO_DUE_AT}`,
        // `"${end}">=${JobTasksFilterType.SCHEDULED_DATE}&&"${end}"<=${JobTasksFilterType.NEXT_TRIGGER_INFO_DUE_AT}`,
        // `"${start}"<=${JobTasksFilterType.SCHEDULED_DATE}&&"${end}">=${JobTasksFilterType.NEXT_TRIGGER_INFO_DUE_AT}`,
        `"${start}">=${JobTasksFilterType.STARTED_AT}&&"${start}"<=${JobTasksFilterType.COMPLETED_AT}`,
        `"${end}">=${JobTasksFilterType.STARTED_AT}&&"${end}"<=${JobTasksFilterType.COMPLETED_AT}`,
        `"${start}"<=${JobTasksFilterType.STARTED_AT}&&"${end}">=${JobTasksFilterType.COMPLETED_AT}`
      ].join('||')})`
    }
  }
  if (searchText) {
    responseOptions = {
      ...responseOptions,
      [ListFilterTypeExpression.SearchText]: searchText
    }
  }
  return responseOptions
}

export const mockItemAPICall = (
  resource: ResourceType,
  params: TItemRequestQueryParams
): Promise<AxiosResponse> => {
  const data = {
    value: generateRandomResources(resource, 1)[0]
  }
  return new Promise((resolve) => {
    const timeoutID = setTimeout(() => {
      resolve({
        data,
        status: StatusCodeType.Success,
        statusText: '',
        headers: new AxiosHeaders(),
        config: { headers: new AxiosHeaders() }
      })
      clearTimeout(timeoutID)
    }, 2000)
  })
}

export const mockListAPICall = (
  resourceType: ResourceType,
  params: TListRequestQueryParams
): Promise<AxiosResponse> => {
  const data = {
    offset: params['Paging.Offset'] ?? DEFAULT_PAGE_OFFSET,
    limit: params['Paging.Limit'] ?? DEFAULT_PAGE_LIMIT,
    responseRowCount: params['Paging.Offset'],
    size: 1000,
    value: generateRandomResources(resourceType, params['Paging.Limit'] ?? DEFAULT_PAGE_LIMIT)
  }

  return new Promise((resolve) => {
    resolve({
      data,
      status: StatusCodeType.Success,
      statusText: '',
      headers: new AxiosHeaders(),
      config: { headers: new AxiosHeaders() }
    })
  })
}

export const mockUpdateItemAPICall = (item: any): Promise<AxiosResponse> => {
  return new Promise((resolve) => {
    const timeoutID = setTimeout(() => {
      resolve({
        data: item,
        status: StatusCodeType.Success,
        statusText: '',
        headers: new AxiosHeaders(),
        config: { headers: new AxiosHeaders() }
      })
      clearTimeout(timeoutID)
    }, 5000)
  })
}
